import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createUser,
  resumeGame,
  getMissionAccomplishments,
} from "../../api/GeneralApis/api";

// Thunk for registering a user
export const registerUserThunk = createAsyncThunk(
  "user/registerUser",
  async (_, { rejectWithValue }) => {
    try {
      const data = await createUser();
      return data.user;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk for resuming the game
export const resumeGameThunk = createAsyncThunk(
  "user/resumeGame",
  async ({ authToken, systemName }, { rejectWithValue, getState }) => {
    try {
      const data = await resumeGame({ authToken, systemName });

      if (!data.user) {
        throw new Error("No user data in response");
      }
      // Return user data with the input authToken since API doesn't provide it
      return {
        ...data.user,
        authToken, // Preserve the input authToken
      };
    } catch (error) {
      console.error(
        "resumeGameThunk error:",
        error.response?.data || error.message
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk for getting mission accomplishments
export const getMissionAccomplishmentsThunk = createAsyncThunk(
  "user/getMissionAccomplishments",
  async ({ authToken, systemName }, { rejectWithValue }) => {
    try {
      const data = await getMissionAccomplishments({
        authToken,
        systemName,
      });

      return data.accomplishmentsManifest; // Return the accomplishmentsManifest directly
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Load user credentials from local storage
const loadUserFromStorage = () => {
  const storedUser = localStorage.getItem("userCredentials");
  return storedUser
    ? JSON.parse(storedUser)
    : {
        authToken: null,
        systemName: null,
        accomplishmentsManifest: {},
      };
};

// Create a slice to manage state
const userSlice = createSlice({
  name: "user",
  initialState: {
    user: loadUserFromStorage(),
    loading: false,
    error: null,
  },
  reducers: {
    clearUser: (state) => {
      state.user = {
        authToken: null,
        systemName: null,
        accomplishmentsManifest: {},
      };
      localStorage.removeItem("userCredentials");

      localStorage.removeItem("clueState");
    },
    updateAccomplishments: (state, action) => {
      state.user.accomplishmentsManifest = action.payload;
      localStorage.setItem("userCredentials", JSON.stringify(state.user));
    },
    setAuthFromLogin: (state, action) => {
      const { authToken, systemName, accomplishmentsManifest } = action.payload;
      if (authToken) state.user.authToken = authToken;
      if (systemName) state.user.systemName = systemName;
      if (accomplishmentsManifest) {
        state.user.accomplishmentsManifest = accomplishmentsManifest;
      }
      localStorage.setItem("userCredentials", JSON.stringify(state.user));
    },
  },
  extraReducers: (builder) => {
    // Register User
    builder
      .addCase(registerUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUserThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          authToken: action.payload.authToken,
          systemName: action.payload.systemName,
          accomplishmentsManifest: action.payload.accomplishmentsManifest || {},
        };

        localStorage.setItem("userCredentials", JSON.stringify(state.user));
      })
      .addCase(registerUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resume Game
      .addCase(resumeGameThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resumeGameThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          authToken: action.payload.authToken,
          systemName: action.payload.systemName,
          accomplishmentsManifest: action.payload.accomplishmentsManifest || {},
        };

        localStorage.setItem("userCredentials", JSON.stringify(state.user));
      })
      .addCase(resumeGameThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Mission Accomplishments
      .addCase(getMissionAccomplishmentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMissionAccomplishmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user.accomplishmentsManifest = action.payload || {};

        localStorage.setItem("userCredentials", JSON.stringify(state.user));
      })
      .addCase(getMissionAccomplishmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUser, updateAccomplishments, setAuthFromLogin } =
  userSlice.actions;
export default userSlice.reducer;
